import { type ReactNode } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  BadgePercent,
  BarChart3,
  BookUser,
  CalendarDays,
  Clock,
  CreditCard,
  Network,
  Lock,
  Package,
  Percent,
  ReceiptText,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { rangeToSearch, useReportRange } from '@/hooks/useReportRange'
import { useBusinessName } from '@/hooks/useBusinessName'
import { DateRangeFilter } from './DateRangeFilter'
import { OverviewTab } from './OverviewTab'
import { DailySalesTab } from './DailySalesTab'
import { AdjustmentsTab } from './AdjustmentsTab'
import { HoursTab } from './HoursTab'
import { ItemsTab } from './ItemsTab'
import { TablesTab } from './TablesTab'
import { TaxSummaryTab } from './TaxSummaryTab'
import { StaffPerformanceTab } from './StaffPerformanceTab'
import { RefundLogsTab } from './RefundLogsTab'
import { CardPromosTab } from './CardPromosTab'
import { DealsTab } from './DealsTab'
import { HouseAccountsTab } from './HouseAccountsTab'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
import { FiscalQueueTab } from './FiscalQueueTab'
import { DayHistoryPage } from '@/components/admin/dayclose/DayHistoryPage'
import {
  ReportsExportOutlet,
  ReportsExportSlotProvider,
} from './ReportsExportSlot'
import type { ReportTab, ReportsSearch } from './reportsRouteSearch'
// Kitchen flow Stage 3 — owner reports, flag-gated.
import { KitchenFlowReportsTab } from './KitchenFlowReportsTab'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { FR } from '@/lib/inventoryFlow/flowReportStrings'

type TabId = ReportTab

const TABS: { id: TabId; label: string; icon: ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'daily', label: 'Daily Sales', icon: <CalendarDays className="h-4 w-4" /> },
  // F-FE-N212 — discounts by source + custom / waived service charge, day by day.
  { id: 'adjustments', label: 'Discounts & Service Charge', icon: <BadgePercent className="h-4 w-4" /> },
  { id: 'hours', label: 'Hours', icon: <Clock className="h-4 w-4" /> },
  { id: 'items', label: 'Items', icon: <Package className="h-4 w-4" /> },
  { id: 'tables', label: 'Tables & Parties', icon: <Users className="h-4 w-4" /> },
  { id: 'staff', label: 'Staff', icon: <UserCog className="h-4 w-4" /> },
  { id: 'tax', label: 'Tax', icon: <Percent className="h-4 w-4" /> },
  { id: 'day-close', label: 'Day Close', icon: <Lock className="h-4 w-4" /> },
  { id: 'refunds', label: 'Refund Logs', icon: <ReceiptText className="h-4 w-4" /> },
  { id: 'card-promos', label: 'Card Promos', icon: <CreditCard className="h-4 w-4" /> },
  // F-DEAL-N5 — combos, automatic discounts, promo codes, special prices, upsells.
  { id: 'deals', label: 'Deals & offers', icon: <BadgePercent className="h-4 w-4" /> },
  // F-MM-N30 — filtered out below when both house-account kinds are off.
  { id: 'accounts', label: 'Staff & Tabs', icon: <BookUser className="h-4 w-4" /> },
  { id: 'fiscal', label: 'Fiscal Queue', icon: <ShieldCheck className="h-4 w-4" /> },
  // Kitchen flow Stage 3 — filtered out below unless kitchen_flow is on.
  { id: 'kitchen-flow', label: FR.tab, icon: <Network className="h-4 w-4" /> },
]

/**
 * The Reports & Analytics page shell. Owns the global date-range filter
 * (mirrored to URL state) and the tab navigation. Each tab is a small,
 * data-loading sibling that uses the same range hook.
 *
 * Per-day order browsing (PRA, guest receipts) lives under **Daily Sales**:
 * use “Drill in” on a row or `?tab=daily&day=YYYY-MM-DD` in the URL.
 */
export function ReportsShell() {
  const range = useReportRange()
  const kitchenFlow = useKitchenFlowEnabled()
  const houseAccounts = useHouseAccountsEnabled()
  // `?tab=` and `?day=` query params — see routes/admin/reports.tsx
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch
  const navigate = useNavigate()

  /**
   * The URL is the ONLY source of truth for the active tab (F-FE-N146).
   *
   * This was React state seeded from `search.tab` and re-synced by an effect
   * keyed on `[search.tab]`, while the tab buttons set that state without ever
   * writing the URL. One manual tab click put the two out of step, and from
   * then on an Overview drill row that navigated to the tab already named in
   * the URL (daily -> daily) changed no dependency, so the effect never ran
   * and the click did nothing until a refresh re-seeded the state. Deriving it
   * makes that divergence unrepresentable rather than merely fixed, and it is
   * the pattern `StoreInventoryDashboard` already uses.
   */
  const tab: TabId = search.tab ?? 'overview'
  // A kitchen-flow link on a tenant without the flag shows Overview. The
  // Staff & Tabs switch is read from settings: until they load, keep the tab,
  // or a cold link to it (Manage Staff's "All months in Reports") was bounced
  // to Overview and the URL rewritten (F-MM-N31).
  const shown: TabId =
    (tab === 'kitchen-flow' && !kitchenFlow) || (tab === 'accounts' && !houseAccounts.any && !houseAccounts.isLoading)
      ? 'overview'
      : tab
  const setTab = (next: TabId) => {
    navigate({
      to: '/admin/reports',
      // Spread `prev` so a drill facet (pay / ot / sub) survives a tab change,
      // then re-stamp the period on top. `useReportRange` writes preset/from/to
      // with a raw `history.replaceState`, which the router does not observe,
      // so the router's `prev` can be stale about the period and spreading it
      // alone would strip the operator's selected range out of the URL — the
      // same reason the expense dashboard's `go()` re-stamps. Idempotent: it
      // writes the range already on screen.
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        ...rangeToSearch(range),
        tab: next,
      }),
      // A tab click is a view toggle, not a navigation, so it should not add a
      // history entry. A drill keeps the default push, so Back still returns
      // from Daily Sales to Overview.
      replace: true,
    })
  }

  // Pulled from Admin → Settings via the shared `useBusinessName` hook,
  // which also drives the PDF-export footer (printReport.ts) and the CSV
  // exports' brand row (backend reports.go). Single source of truth keeps
  // every surface — header, PDF, CSV — in agreement after a rename.
  // Returns null when neither restaurant_name nor receipt_business_name is
  // configured, so we can drop the trailing "for X" cleanly on a fresh
  // install instead of rendering an awkward placeholder.
  const businessName = useBusinessName()
  const subtitle = businessName
    ? `Sales, items, tables and more for ${businessName}`
    : 'Sales, items, tables and more'

  return (
    <ReportsExportSlotProvider>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reports &amp; Analytics</h1>
            <p className="mt-1 text-muted-foreground">{subtitle}</p>
          </div>
          {tab !== 'day-close' && tab !== 'fiscal' && (
            <div className="lg:pt-1">
              <DateRangeFilter range={range} />
            </div>
          )}
        </div>

        {/* Tabs row + export outlet share one horizontal band. The outlet
            renders OUTSIDE the muted tab container so the Export button
            visually reads as a page action, not as another tab. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
            {TABS.filter(t => (t.id !== 'kitchen-flow' || kitchenFlow) && (t.id !== 'accounts' || houseAccounts.any)).map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                  shown === t.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
          <ReportsExportOutlet />
        </div>

        {shown === 'overview' && <OverviewTab range={range} />}
        {tab === 'daily' && <DailySalesTab range={range} />}
        {tab === 'adjustments' && <AdjustmentsTab range={range} />}
        {tab === 'hours' && <HoursTab range={range} />}
        {tab === 'items' && <ItemsTab range={range} />}
        {tab === 'tables' && <TablesTab range={range} />}
        {tab === 'staff' && <StaffPerformanceTab range={range} />}
        {tab === 'tax' && <TaxSummaryTab range={range} />}
        {tab === 'day-close' && <DayHistoryPage />}
        {tab === 'refunds' && <RefundLogsTab range={range} />}
        {tab === 'card-promos' && <CardPromosTab range={range} />}
        {tab === 'deals' && <DealsTab range={range} />}
        {shown === 'accounts' && <HouseAccountsTab range={range} />}
        {tab === 'fiscal' && <FiscalQueueTab range={range} />}
        {tab === 'kitchen-flow' && kitchenFlow && <KitchenFlowReportsTab range={range} />}
      </div>
    </ReportsExportSlotProvider>
  )
}
